import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';

export interface WhatsAppTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  clicks: number | string;
  status: 'Enabled' | 'In Review' | 'Rejected' | 'Disabled';
  jsonPayload?: any;
}

const MOCK_TEMPLATES: WhatsAppTemplate[] = [
  { id: '1', name: 'msg91marketing', category: 'MARKETING', language: 'En', clicks: '-', status: 'Enabled' },
  { id: '2', name: 'build', category: 'MARKETING', language: 'En', clicks: '-', status: 'Enabled' },
  { id: '3', name: 'loyalty_', category: 'MARKETING', language: 'En', clicks: '-', status: 'Enabled' },
  { id: '4', name: 'test_template', category: 'AUTHENTICATION', language: 'En_US', clicks: '-', status: 'Enabled' },
  { id: '5', name: 'diwali_sale_offer', category: 'MARKETING', language: 'Hi', clicks: '-', status: 'In Review' },
  { id: '6', name: 'dynamic_url_testing', category: 'UTILITY', language: 'En', clicks: 'Yes 1', status: 'Enabled' },
  { id: '7', name: 'spam_template_1', category: 'MARKETING', language: 'En', clicks: '12', status: 'Rejected' },
];

@Component({
  selector: 'app-msg91',
  templateUrl: './msg91.component.html',
  styleUrls: ['./msg91.component.css']
})
export class Msg91Component implements OnInit {

  // --- View State ---
  view: 'list' | 'create' = 'list';

  // --- Listing Data ---
  displayedColumns: string[] = ['name', 'category', 'language', 'clicks', 'json', 'status', 'actions'];
  dataSource: MatTableDataSource<WhatsAppTemplate>;

  first: number = 0;
  tableSize: number = 10;

  // --- Form Data ---
  templateForm!: FormGroup;

  categories = ['Marketing', 'Utility', 'Authentication'];
  languages = ['English', 'Hindi', 'Telugu', 'Tamil', 'Kannada'];
  headerTypes = ['None', 'Text', 'Image', 'Video', 'Document'];

  constructor(private fb: FormBuilder, public dialog: MatDialog) {
    this.dataSource = new MatTableDataSource(MOCK_TEMPLATES);
  }
  ngOnInit(): void {
    this.initForm();
  }

  onTableDataChange(event: any) {
    this.first = event.first;
    this.tableSize = event.rows;
  }

  // --- Listing Methods ---
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    this.first = 0; // reset to first page
  }

  openCreate() {
    this.initForm();
    this.view = 'create';
  }

  closeCreate() {
    this.view = 'list';
  }

  // --- Form Methods ---
  initForm() {
    this.templateForm = this.fb.group({
      name: ['msg91 marketing', Validators.required],
      category: ['Marketing', Validators.required],
      language: ['English', Validators.required],
      templateType: ['Custom', Validators.required], // Custom, Product, Carousel
      headerType: ['Image'],
      headerText: [''],
      bodyText: ['Hi {{1}},\nLooking to engage your customers faster and smarter?\nWith MSG91, you can send SMS, WhatsApp, email, and voice messages.', Validators.required],
      footerText: [''],
      buttons: this.fb.array([]),
      cards: this.fb.array([
        this.fb.group({
          body: ['']
        })
      ]) // For carousel
    });
  }

  get buttons() {
    return this.templateForm.get('buttons') as FormArray;
  }

  get cards() {
    return this.templateForm.get('cards') as FormArray;
  }

  addCard() {
    this.cards.push(this.fb.group({
      body: ['']
    }));
  }

  removeCard(index: number) {
    this.cards.removeAt(index);
  }

  addButton(type: string) {
    if (this.buttons.length >= 3) return;
    this.buttons.push(this.fb.group({
      type: [type],
      text: [''],
      url: [''],
      phoneNumber: ['']
    }));
  }

  removeButton(index: number) {
    this.buttons.removeAt(index);
  }

  addVariable() {
    const currentBody = this.templateForm.get('bodyText')?.value || '';
    const match = currentBody.match(/\{\{(\d+)\}\}/g);
    let nextVar = 1;
    if (match) {
      const vars = match.map((m: any) => parseInt(m.replace(/\{|\}/g, '')));
      nextVar = Math.max(...vars) + 1;
    }
    this.templateForm.get('bodyText')?.setValue(currentBody + ` {{${nextVar}}}`);
  }

  applyFormat(format: string, textarea: HTMLTextAreaElement, control: any) {
    if (!textarea || !control) return;

    let wrapChar = '';
    if (format === 'bold') wrapChar = '*';
    if (format === 'italic') wrapChar = '_';
    if (format === 'strikethrough') wrapChar = '~';
    if (format === 'emoji') wrapChar = '🙂';

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = textarea.value;

    let newText = '';
    let newCursorPos = start;

    if (format === 'emoji') {
        newText = currentText.substring(0, start) + wrapChar + currentText.substring(end);
        newCursorPos = start + wrapChar.length;
    } else {
        const selectedText = currentText.substring(start, end);
        newText = currentText.substring(0, start) + wrapChar + selectedText + wrapChar + currentText.substring(end);
        newCursorPos = end + (wrapChar.length * 2);
    }

    control.setValue(newText);
    
    // Restore focus and cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }

  onSave() {
    if (this.templateForm.valid) {
      console.log('Saved:', this.templateForm.value);
      this.view = 'list';
    } else {
      this.templateForm.markAllAsTouched();
    }
  }

  // --- Helper Methods ---
  getStatusClass(status: string) {
    return {
      'status-enabled': status === 'Enabled',
      'status-review': status === 'In Review',
      'status-rejected': status === 'Rejected',
      'status-disabled': status === 'Disabled',
    };
  }
}
